import React from "react";
import { X, ArrowsClockwise, Trash, Warning } from "@phosphor-icons/react";
import { Button } from "../../../components/ui/Button";

/**
 * Iter 19.63.66 — Reset-Dialog für Step 3.
 *
 * Zeigt zwei Aktionen:
 *  1. „Eigene Änderungen verwerfen" → behält Dokumente, lässt die KI auf
 *     denselben Files erneut laufen (User-Edits werden überschrieben durch
 *     frische LLM-Extraktion).
 *  2. „Alles verwerfen (Neustart)" → verwirft Dokumente, Zeilen,
 *     Ausbildungs-Bonuszeiten und KI-Info. User startet Step 3 komplett neu.
 *
 * Beide Aktionen sind irreversibel; daher dieses Bestätigungs-Modal.
 */
export default function ResetDialog({
  open,
  onClose,
  onResetEdits,
  onResetAll,
  hasFiles = false,
  hasRows = false,
  busy = false,
}) {
  if (!open) return null;

  const canSoftReset = hasFiles && hasRows;

  return (
    <div
      data-testid="step3-reset-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-ink-900/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="step3-reset-dialog-title"
    >
      <div
        className="relative w-full max-w-lg bg-white border border-ink-200 rounded-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ink-200">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
              Schritt 3 zurücksetzen
            </div>
            <h3
              id="step3-reset-dialog-title"
              className="font-cabinet text-xl text-ink-900 tracking-tight mt-0.5"
            >
              Wie möchten Sie zurücksetzen?
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="step3-reset-dialog-close"
            aria-label="Schließen"
            className="text-warm-600 hover:text-ink-900 transition-crisp shrink-0"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 text-sm text-ink-800">
          <p className="text-warm-700 leading-relaxed">
            Sie haben zwei Möglichkeiten. Beide Aktionen lassen sich nicht
            rückgängig machen.
          </p>

          {/* Option 1: Soft Reset — eigene Änderungen verwerfen */}
          <div className="border border-ink-200 rounded-sm p-4 hover:border-brand-300 transition-crisp">
            <div className="flex items-start gap-3">
              <ArrowsClockwise
                size={20}
                weight="duotone"
                className="text-brand-500 shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <div className="font-medium text-ink-900 mb-1">
                  Eigene Änderungen verwerfen
                </div>
                <p className="text-[12px] text-warm-700 leading-relaxed mb-3">
                  Ihre hochgeladenen Dokumente bleiben erhalten. Die Auswertung erfolgt
                  sie erneut aus &mdash; alle manuellen Änderungen
                  (Häkchen, korrigierte Datums, Begründungen) werden dabei
                  überschrieben.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onResetEdits}
                  disabled={busy || !canSoftReset}
                  data-testid="step3-reset-dialog-soft"
                  title={
                    !canSoftReset
                      ? "Nicht möglich: Es sind keine hochgeladenen Dokumente vorhanden, auf die die Auswertung erneut angewendet werden könnte."
                      : "Auswertung auf denselben Dokumenten neu starten"
                  }
                >
                  <ArrowsClockwise size={13} weight="bold" />
                  Eigene Änderungen verwerfen
                </Button>
                {!canSoftReset && (
                  <div className="mt-2 text-[11px] text-warm-500 italic">
                    Diese Option ist nur verfügbar, wenn Dokumente hochgeladen
                    UND Zeilen vorhanden sind.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Option 2: Hard Reset — alles verwerfen */}
          <div className="border border-status-abgelehnt-border/60 rounded-sm p-4 bg-status-abgelehnt-bg/30 hover:border-status-abgelehnt-border transition-crisp">
            <div className="flex items-start gap-3">
              <Trash
                size={20}
                weight="duotone"
                className="text-status-abgelehnt-text shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <div className="font-medium text-ink-900 mb-1">
                  Alles verwerfen (Neustart)
                </div>
                <p className="text-[12px] text-warm-700 leading-relaxed mb-3">
                  Alle hochgeladenen Dokumente, alle Beschäftigungszeiten und
                  alle Ausbildungs-Bonuszeiten werden verworfen. Sie beginnen
                  Schritt 3 von vorne.
                </p>
                <div className="flex items-start gap-1.5 text-[11px] text-status-abgelehnt-text mb-3">
                  <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                  <span>
                    Diese Aktion löscht den vollständigen Step-3-Zustand und
                    kann nicht rückgängig gemacht werden.
                  </span>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={onResetAll}
                  disabled={busy}
                  data-testid="step3-reset-dialog-hard"
                >
                  <Trash size={13} weight="bold" />
                  Alles verwerfen (Neustart)
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-200 bg-ink-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="step3-reset-dialog-cancel"
            className="text-sm font-medium text-warm-700 hover:text-ink-900 transition-crisp px-3 py-1.5"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
